/**
 * Textteile der Koerperkarte (F-34 C2/C3): was beim Antippen eines Muskels
 * steht - je Ansicht -, die Gesamtraenge und die Grundzeiten der Regeneration.
 */
import { BedDouble } from 'lucide-react';

import { einstellungenAendern, muedeUmschalten } from '@/db/repositories/training';
import type { MuskelId } from '@/domain/training/muskeln';
import { STATUS_TEXT, type MuskelStatus, type MuskelWert } from '@/domain/training/muskelcheck';
import { ERHOLUNG_TEXT, STANDARD_REGENERATION, type Erholung, type RegenerationsWerte } from '@/domain/training/regeneration';
import { RANG_TEXT, rangVon, type RangErgebnis } from '@/domain/training/rang';
import type { Uebung } from '@/domain/training/typen';
import { LinienDiagramm } from '@/ui/composites/LinienDiagramm';
import { formatDe, today } from '@/lib/datetime';
import { RANG_FARBE } from '@/modules/training/koerper/farben';
import { erholtAbText } from '@/modules/training/koerper/hilfen';
import { zahl1 } from '@/modules/training/format';
import { cn } from '@/lib/utils/cn';

const box = 'rounded-md bg-surface-raised/60 px-3 py-2 text-sm';

export function VolumenDetails({ name, w, ziel, st, bibliothek }: { name: string; w: MuskelWert; ziel: { saetze: number; tage: number }; st: MuskelStatus; bibliothek: ReadonlyMap<string, Uebung> }) {
  return (
    <div role="status" className={box}>
      <p className="font-medium">{name}: {STATUS_TEXT[st]}</p>
      <p className="text-ink-muted">{zahl1(w.saetze)} Sätze an {zahl1(w.tage)} {w.tage === 1 ? 'Tag' : 'Tagen'} · Ziel {ziel.saetze} Sätze an {ziel.tage} Tagen</p>
      {w.uebungen.length > 0 && <p className="text-[12px] text-ink-muted">Übungen: {w.uebungen.map((id) => bibliothek.get(id)?.name ?? id).join(', ')}</p>}
    </div>
  );
}


export function RegenerationDetails({ name, e, muskeln, muede }: { name: string; e: Erholung; muskeln: MuskelId[]; muede: ReadonlySet<MuskelId> }) {
  const an = muskeln.every((m) => muede.has(m));
  return (
    <div role="status" className={cn(box, 'flex flex-col gap-1.5')}>
      <p className="font-medium">{name}: {e.prozent} % – {ERHOLUNG_TEXT[e.stufe]}{e.gefuehl ? ' (eigenes Gefühl)' : ''}</p>
      <p className="text-ink-muted">{e.stufe === 'erholt' ? (e.letzte ? 'Letzte Belastung ist verarbeitet.' : 'In den letzten 7 Tagen nicht belastet.') : erholtAbText(e.erholtAb)}</p>
      <button type="button" aria-pressed={an} onClick={async () => { for (const m of muskeln) if (muede.has(m) === an) await muedeUmschalten(today(), m); }}
        className={cn('flex h-9 items-center gap-1.5 self-start rounded-md border px-2.5 text-[12px]', an ? 'border-accent bg-accent/10 text-ink' : 'border-line text-ink-muted hover:text-ink')}>
        <BedDouble className="size-4" /> Muskelkater / fühlt sich müde an (heute)
      </button>
    </div>
  );
}

export function RangDetails({ r, name, verlauf }: { r: RangErgebnis; name: string; verlauf: { woche: string; wert: number | null }[] }) {
  return (
    <div role="status" className={cn(box, 'flex flex-col gap-1.5')}>
      <p className="font-medium">{name}: {r.rang ? `${RANG_TEXT[r.rang]} · ${zahl1(r.wert!)} von 10` : 'noch nicht eingestuft'}</p>
      <ul className="text-[12px] text-ink-muted">
        <li>Kraft (50 %): {r.kraft.wert !== null ? `${zahl1(r.kraft.wert)} – ` : ''}{r.kraft.text}</li>
        <li>Beständigkeit (30 %): {r.bestaendigkeit.wert !== null ? `${zahl1(r.bestaendigkeit.wert)} – ` : ''}{r.bestaendigkeit.text}</li>
        <li>Entwicklung (20 %): {r.entwicklung.wert !== null ? `${zahl1(r.entwicklung.wert)} – ` : ''}{r.entwicklung.text}</li>
      </ul>
      {verlauf.some((v) => v.wert !== null) && (
        <LinienDiagramm titel={`Rang über die Zeit: ${name}`} min={1} max={10} ticks={[2, 4, 6, 8, 10]}
          punkte={verlauf.map((v) => ({ key: v.woche, titel: `Woche ab ${formatDe(v.woche, 'dd.MM.')}`, kurz: formatDe(v.woche, 'dd.MM.') }))}
          reihen={[{ id: 'r', name: 'Rangwert 1–10', platz: 2, werte: verlauf.map((v) => v.wert) }]} />
      )}
    </div>
  );
}

export function Gesamtraenge({ gesamt, ausdauer }: { gesamt: Record<'gesamt' | 'oberkoerper' | 'unterkoerper' | 'rumpf', number | null>; ausdauer: { wert: number | null; text: string } }) {
  const zeilen: [string, number | null, string?][] = [
    ['Gesamt', gesamt.gesamt], ['Oberkörper', gesamt.oberkoerper], ['Unterkörper', gesamt.unterkoerper], ['Rumpf', gesamt.rumpf], ['Ausdauer', ausdauer.wert, ausdauer.text],
  ];
  return (
    <ul aria-label="Gesamtränge" className="grid grid-cols-2 gap-1.5 text-[12px] sm:grid-cols-5">
      {zeilen.map(([t, w, hint]) => (
        <li key={t} title={hint} className="flex flex-col rounded-md bg-surface-raised/60 px-2 py-1.5">
          <span className="text-ink-muted">{t}</span>
          {w === null
            ? <span className="text-ink-faint">nicht eingestuft</span>
            : <span className="font-medium"><span className="mr-1 inline-block size-2.5 rounded-full" style={{ backgroundColor: RANG_FARBE[rangVon(w)] }} />{RANG_TEXT[rangVon(w)]} · {zahl1(w)}</span>}
        </li>
      ))}
    </ul>
  );
}

export function RegenerationsEinstellung({ werte }: { werte?: RegenerationsWerte }) {
  const w = werte ?? STANDARD_REGENERATION;
  return (
    <details className="text-[12px] text-ink-muted">
      <summary className="cursor-pointer">Grundzeiten der Schätzung anpassen</summary>
      <div className="mt-1.5 flex flex-wrap gap-2">
        {(['gross', 'mittel', 'klein'] as const).map((k) => (
          <label key={k} className="flex items-center gap-1">{k === 'gross' ? 'Große' : k === 'mittel' ? 'Mittlere' : 'Kleine'} Muskeln
            <input key={`${k}${w[k]}`} defaultValue={w[k]} inputMode="numeric" aria-label={`Grundzeit ${k} in Stunden`}
              onBlur={(e) => { const n = Math.round(Number(e.target.value)); if (n >= 12 && n <= 168) void einstellungenAendern({ regeneration: { ...w, [k]: n } }); }}
              className="h-7 w-14 rounded-md border border-line bg-surface px-1 text-center tabular-nums text-ink" /> h
          </label>
        ))}
      </div>
      <p className="mt-1">Umfang (0,6 + 0,08 je Satz) und „Wie leicht fiel es?“ verlängern oder verkürzen die Zeit. Quellen: siehe ADR 0007.</p>
    </details>
  );
}

